import * as React from 'react';
import {
  Menu,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  ListSubheader,
  ListItemSecondaryAction,
  Avatar,
  ListItemAvatar,
  ListItem,
  Box,
  Icon,
  Badge,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { getTimeDiff } from './utils';
import { HubConnectionBuilder, HttpTransportType, HubConnection } from '@microsoft/signalr';
import { AxiosResponse } from 'axios';
import fetchClient from '../../services/axios';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { alertError, alertWarning } from '../../slices/global/alertSlice';
import useConfirm from '../../hooks/useConfirm';
import { selectClaims, selectUser } from '../../slices/global/authSlice';
import RestrictedAccess from '../RestrictedAccess';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import OverflowTooltip from '../OverflowTip';

interface MessageProps {
  id: number;
  severityId: 1 | 2 | 3 | 4;
  severity: 'Critical' | 'Error' | 'Warning' | 'Info';
  isDismissed: boolean;
  message: string;
  created: string;
}

interface Props extends ListChildComponentProps {
  dismissNotification: (id: number) => void;
}

function renderRow(props: Props) {
  const { index, style, data, dismissNotification } = props;

  const item = data[index] as MessageProps;

  return (
    <ListItem style={style} key={index} component="div" disablePadding>
      <ListItemButton
        onClick={() => dismissNotification(item.id)}
        sx={{
          height: '100%',
        }}
      >
        <ListItemAvatar>
          <Avatar
            sx={{
              bgcolor:
                item.severityId === 1 || item.severityId === 2
                  ? 'red'
                  : item.severityId === 3
                  ? 'orangeRed'
                  : 'lightBlue',
            }}
          >
            <Icon>
              {item.severityId === 1
                ? 'priority_high'
                : item.severityId === 2
                ? 'error'
                : item.severityId === 3
                ? 'warning'
                : 'info'}
            </Icon>
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={<OverflowTooltip primary={item.message} />}
          secondary={getTimeDiff(item.created)}
        />
      </ListItemButton>
    </ListItem>
  );
}
export default function Notifications() {
  const dispatch = useAppDispatch();
  const { promptConfirm } = useConfirm();
  const user = useAppSelector(selectUser);
  const claims = useAppSelector(selectClaims);
  const [connection, setConnection] = React.useState<HubConnection | null>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [messages, setMessages] = React.useState<MessageProps[] | null>();
  const [loading, setLoading] = React.useState<boolean>(false);
  const open = Boolean(anchorEl);
  const doesUserHaveAccess =
    user &&
    claims.filter((x) => x.type === 'UI-Access' && x.value === 'Notifications' && x.isActive)
      .length > 0;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getNotifications = React.useCallback(
    async (controller: AbortController) => {
      await fetchClient
        .get('/Hub/Notifications', {
          signal: controller.signal,
        })
        .then((response: AxiosResponse<MessageProps[] | null>) => {
          setMessages(response.data);
        })
        .catch(function (err) {
          if (err.message !== 'canceled')
            dispatch(alertError(`Failed to retrieve notifications. Reason: ${err}`));
        });
    },
    [dispatch],
  );

  const dismissNotification = async (id: number) => {
    const isConfirmed = await promptConfirm(
      'Dismiss notification?',
      'Do you wish to continue?',
      'Accept',
      'Cancel',
    );

    if (!isConfirmed) return;

    setLoading(true);
    await fetchClient
      .post('/Notification/DismissById', null, {
        params: { id: id },
      })
      .then((response: AxiosResponse) => {
        if (!response.data.isSuccessStatusCode) {
          dispatch(alertWarning(`Failed to dismiss. Reason: ${response.data.reasonPhrase}`));
        }
      })
      .catch(function (err) {
        if (err.message !== 'canceled') dispatch(alertError(`Failed to dismiss. Reason: ${err}`));
      })
      .finally(() => setLoading(false));
  };

  const dismissAllNotifications = async () => {
    const isConfirmed = await promptConfirm(
      'Dismiss all notifications?',
      'Do you wish to continue?',
      'Accept',
      'Cancel',
    );

    if (!isConfirmed) return;

    setLoading(true);
    await fetchClient
      .post('/Notification/DismissAll', null)
      .then((response) => {
        !response.data.isSuccessStatusCode &&
          dispatch(
            alertError(
              `Error while trying to dismiss notification(s). Reason: ${response.data.reasonPhrase}`,
            ),
          );
      })
      .catch(function (err) {
        if (err.message !== 'canceled')
          dispatch(alertError(`Error while trying to dismiss notification(s). ${err}`));
      })
      .finally(() => setLoading(false));
  };

  React.useEffect(() => {
    const conn = new HubConnectionBuilder()
      .withUrl('https://masterapi.autoshack.com/notifications', {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect()
      .build();

    setConnection(conn);
  }, []);

  React.useEffect(() => {
    const controller = new AbortController();

    if (connection && doesUserHaveAccess) {
      connection
        .start()
        .then(() => {
          getNotifications(controller);

          connection.on('refreshNotifications', () => {
            getNotifications(controller);
          });
        })
        .catch(function (err: any) {
          dispatch(alertError(`Failed to connect to the server. Reason: ${err}`));
        });
    }

    return () => {
      if (connection) connection.stop();
      controller.abort();
    };
  }, [connection, getNotifications, dispatch, doesUserHaveAccess]);

  return (
    <RestrictedAccess type="UI-Access" value="Notifications" showAlert={false}>
      <ListItemButton onClick={handleClick}>
        <ListItemIcon>
          <Badge badgeContent={messages ? messages.length : 0} color="primary">
            <Icon>
              {messages && messages.length > 0 ? 'notifications_active' : 'notifications'}
            </Icon>
          </Badge>
        </ListItemIcon>
        <ListItemText
          primary="Notifications"
          primaryTypographyProps={{
            fontSize: 12,
          }}
        />
      </ListItemButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ width: '100%', height: 800, bgcolor: 'background.paper' }}>
          <ListSubheader sx={{ fontSize: 16 }}>
            Notifications
            <ListItemSecondaryAction>
              <LoadingButton
                loading={loading}
                onClick={dismissAllNotifications}
                disabled={!messages || messages.length === 0}
              >
                Clear
              </LoadingButton>
            </ListItemSecondaryAction>
          </ListSubheader>
          {(!messages || messages?.length === 0) && (
            <ListItem sx={{ width: '100%' }}>
              <ListItemAvatar>
                <Avatar
                  sx={{
                    bgcolor: 'green',
                  }}
                >
                  <Icon>check</Icon>
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={<OverflowTooltip primary={"You're all good, no notifications"} />}
              />
            </ListItem>
          )}
          {messages && messages.length > 0 && (
            <FixedSizeList
              height={750}
              width={1000}
              itemSize={75}
              itemData={messages ?? []}
              itemCount={messages?.length ?? 0}
              overscanCount={5}
            >
              {(props) => renderRow({ ...props, dismissNotification: dismissNotification })}
            </FixedSizeList>
          )}
        </Box>
      </Menu>
    </RestrictedAccess>
  );
}
